import type GraphEngine from './GraphEngine';
import type { Position } from '../types';

const isMobile = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;

export default class Interaction {
  _engine: GraphEngine;
  _selectedNodes: Set<number | string>;
  _selectedEdges: Set<number | string>;
  _hoveredNode: number | string | null;
  _hoveredEdge: number | string | null;
  _dragNode: number | string | null;
  _dragEmitted: boolean;
  _isPanning: boolean;
  _pointerDown: boolean;
  _lastPointer: Position | null;
  _downPointer: Position | null;
  _pinchDist: number;
  _pinchScale: number;
  _activePointers: Map<number, Position>;
  _bound: Record<string, any>;
  _attached: boolean;
  _canvas: HTMLCanvasElement | null;

  constructor(engine: GraphEngine) {
    this._engine = engine;
    this._selectedNodes = new Set();
    this._selectedEdges = new Set();
    this._hoveredNode = null;
    this._hoveredEdge = null;
    this._dragNode = null;
    this._dragEmitted = false;
    this._isPanning = false;
    this._pointerDown = false;
    this._lastPointer = null;
    this._downPointer = null;
    this._pinchDist = 0;
    this._pinchScale = 1;
    this._activePointers = new Map();
    this._bound = {};
    this._attached = false;
    this._canvas = null;
  }

  attach(canvas: HTMLCanvasElement): void {
    this._canvas = canvas;
    const b = this._bound;
    b.pointerdown = (e: PointerEvent) => this._onPointerDown(e);
    b.pointermove = (e: PointerEvent) => this._onPointerMove(e);
    b.pointerup = (e: PointerEvent) => this._onPointerUp(e);
    b.pointercancel = (e: PointerEvent) => this._onPointerUp(e);
    b.wheel = (e: WheelEvent) => this._onWheel(e);

    canvas.addEventListener('pointerdown', b.pointerdown);
    canvas.addEventListener('pointermove', b.pointermove);
    canvas.addEventListener('pointerup', b.pointerup);
    canvas.addEventListener('pointercancel', b.pointercancel);
    canvas.addEventListener('wheel', b.wheel, { passive: false });
    canvas.addEventListener('contextmenu', (e: Event) => e.preventDefault());
    canvas.style.touchAction = 'none';
    this._attached = true;
  }

  _onPointerDown(e: PointerEvent): void {
    this._activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (this._activePointers.size === 2) {
      const pts = Array.from(this._activePointers.values());
      this._pinchDist = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
      this._pinchScale = this._engine._camera.scale;
      this._dragNode = null;
      return;
    }
    this._pointerDown = true;
    this._lastPointer = { x: e.clientX, y: e.clientY };
    this._downPointer = { x: e.clientX, y: e.clientY };
    const canvasPos = this._engine._camera.DOMtoCanvas({ x: e.clientX, y: e.clientY });
    const hitNode = this._hitTestNode(canvasPos.x, canvasPos.y);
    if (hitNode !== null) {
      this._dragNode = hitNode;
      this._dragEmitted = false;
      this._engine._physics.pinNode(hitNode);
      if (!e.ctrlKey && !e.metaKey && !e.shiftKey) {
        this._selectedNodes.clear();
        this._selectedEdges.clear();
      }
      this._selectedNodes.add(hitNode);
      this._engine.emit('selectNode', {
        nodes: Array.from(this._selectedNodes),
        edges: Array.from(this._selectedEdges),
        event: e,
        pointer: { DOM: { x: e.clientX, y: e.clientY }, canvas: canvasPos },
      });
      this._engine.markDirty();
    } else {
      const hitEdge = this._hitTestEdge(canvasPos.x, canvasPos.y);
      if (hitEdge !== null) {
        if (!e.ctrlKey && !e.metaKey && !e.shiftKey) {
          this._selectedNodes.clear();
          this._selectedEdges.clear();
        }
        this._selectedEdges.add(hitEdge);
        this._engine.emit('selectEdge', {
          nodes: [],
          edges: Array.from(this._selectedEdges),
          event: e,
          pointer: { DOM: { x: e.clientX, y: e.clientY }, canvas: canvasPos },
        });
        this._engine.markDirty();
      } else {
        if (this._selectedNodes.size > 0 || this._selectedEdges.size > 0) {
          this._selectedNodes.clear();
          this._selectedEdges.clear();
          this._engine.emit('deselectNode', { nodes: [], edges: [] });
          this._engine.markDirty();
        }
        this._isPanning = true;
      }
    }
  }

  _onPointerMove(e: PointerEvent): void {
    this._activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (this._activePointers.size === 2) {
      const pts = Array.from(this._activePointers.values());
      const dist = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
      if (this._pinchDist > 0) {
        const camera = this._engine._camera;
        camera.scale = this._pinchScale * (dist / this._pinchDist);
        camera.scale = Math.max(0.05, Math.min(camera.scale, 5));
        this._engine.markDirty();
      }
      return;
    }
    if (!this._pointerDown && !isMobile) {
      const canvasPos = this._engine._camera.DOMtoCanvas({ x: e.clientX, y: e.clientY });
      const hitNode = this._hitTestNode(canvasPos.x, canvasPos.y);
      const hitEdge = hitNode === null ? this._hitTestEdge(canvasPos.x, canvasPos.y) : null;
      if (hitNode !== this._hoveredNode) {
        if (this._hoveredNode !== null) this._engine.emit('blurNode', { node: this._hoveredNode });
        this._hoveredNode = hitNode;
        if (hitNode !== null) this._engine.emit('hoverNode', { node: hitNode });
        this._engine.markDirty();
      }
      if (hitEdge !== this._hoveredEdge) {
        if (this._hoveredEdge !== null) this._engine.emit('blurEdge', { edge: this._hoveredEdge });
        this._hoveredEdge = hitEdge;
        if (hitEdge !== null) this._engine.emit('hoverEdge', { edge: hitEdge });
        this._engine.markDirty();
      }
      this._canvas!.style.cursor = (hitNode !== null || hitEdge !== null) ? 'pointer' : 'default';
      return;
    }
    if (!this._lastPointer) return;
    const dx = e.clientX - this._lastPointer.x;
    const dy = e.clientY - this._lastPointer.y;
    this._lastPointer = { x: e.clientX, y: e.clientY };
    if (this._dragNode !== null) {
      const canvasPos = this._engine._camera.DOMtoCanvas({ x: e.clientX, y: e.clientY });
      this._engine._physics.setPosition(this._dragNode, canvasPos.x, canvasPos.y);
      this._engine._physics.tickOnce();
      if (!this._dragEmitted) {
        this._engine.emit('dragStart', { nodeIds: [this._dragNode] });
        this._dragEmitted = true;
      }
    } else if (this._isPanning) {
      const camera = this._engine._camera;
      camera.x -= dx / camera.scale;
      camera.y -= dy / camera.scale;
      this._engine.markDirty();
    }
  }

  _onPointerUp(e: PointerEvent): void {
    this._activePointers.delete(e.pointerId);
    if (this._activePointers.size < 2) this._pinchDist = 0;

    // Detect tap: pointerup without significant drag movement
    const wasTap = this._downPointer !== null &&
      Math.hypot(e.clientX - this._downPointer.x, e.clientY - this._downPointer.y) < 10;

    if (this._dragNode !== null) {
      this._engine._physics.unpinNode(this._dragNode);
      this._dragNode = null;
    }

    if (wasTap) {
      const canvasPos = this._engine._camera.DOMtoCanvas({ x: e.clientX, y: e.clientY });
      const hitNode = this._hitTestNode(canvasPos.x, canvasPos.y);
      const hitEdge = hitNode === null ? this._hitTestEdge(canvasPos.x, canvasPos.y) : null;

      // Suppress the native click event that fires after pointerup.
      // Without this, react-contexify's global handler catches the click
      // and immediately closes the context menu that was just opened.
      if (hitNode !== null || hitEdge !== null) {
        const capture = (ev: Event) => {
          ev.stopPropagation();
          ev.preventDefault();
        };
        this._canvas!.addEventListener('click', capture, { once: true, capture: true });
      }

      this._engine.emit('click', {
        nodes: hitNode !== null ? [hitNode] : [],
        edges: hitEdge !== null ? [hitEdge] : [],
        event: e,
        pointer: { DOM: { x: e.clientX, y: e.clientY }, canvas: canvasPos },
      });
    }

    this._pointerDown = false;
    this._isPanning = false;
    this._lastPointer = null;
    this._downPointer = null;
  }

  _onWheel(e: WheelEvent): void {
    e.preventDefault();
    const camera = this._engine._camera;
    const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
    camera.scale *= zoomFactor;
    camera.scale = Math.max(0.05, Math.min(camera.scale, 5));
    this._engine.markDirty();
  }

  _hitTestNode(cx: number, cy: number): number | string | null {
    const engine = this._engine;
    const physics = engine._physics;
    const nodes = engine._nodes.get();
    const resolver = engine._groupStyleResolver;
    for (let i = nodes.length - 1; i >= 0; i--) {
      const node = nodes[i];
      const idx = physics._idToIndex.get(node.id);
      if (idx === undefined) continue;
      const nx = physics._pos[idx * 2];
      const ny = physics._pos[idx * 2 + 1];
      const style = resolver.resolve(node);
      const r = style.size + 5;
      const ddx = cx - nx;
      const ddy = cy - ny;
      if (ddx * ddx + ddy * ddy <= r * r) return node.id;
    }
    return null;
  }

  _hitTestEdge(cx: number, cy: number): number | string | null {
    const engine = this._engine;
    const physics = engine._physics;
    const edges = engine._edges.get();
    const threshold = 8 / engine._camera.scale;
    const smooth = engine._options.edges?.smooth;
    for (let i = edges.length - 1; i >= 0; i--) {
      const edge = edges[i];
      const fromIdx = physics._idToIndex.get(edge.from);
      const toIdx = physics._idToIndex.get(edge.to);
      if (fromIdx === undefined || toIdx === undefined) continue;
      const x1 = physics._pos[fromIdx * 2];
      const y1 = physics._pos[fromIdx * 2 + 1];
      const x2 = physics._pos[toIdx * 2];
      const y2 = physics._pos[toIdx * 2 + 1];
      let dist: number;
      if (smooth && typeof smooth === 'object') {
        const midX = (x1 + x2) / 2;
        const midY = (y1 + y2) / 2;
        const dx = x2 - x1;
        const dy = y2 - y1;
        const roundness = smooth.roundness || 0.5;
        const cpX = midX + dy * roundness * 0.5;
        const cpY = midY - dx * roundness * 0.5;
        dist = this._pointToQuadraticDist(cx, cy, x1, y1, cpX, cpY, x2, y2);
      } else {
        dist = this._pointToSegmentDist(cx, cy, x1, y1, x2, y2);
      }
      if (dist < threshold) return edge.id!;
    }
    return null;
  }

  _pointToSegmentDist(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
    const ddx = x2 - x1;
    const ddy = y2 - y1;
    const lenSq = ddx * ddx + ddy * ddy;
    if (lenSq === 0) return Math.hypot(px - x1, py - y1);
    let t = ((px - x1) * ddx + (py - y1) * ddy) / lenSq;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(px - (x1 + t * ddx), py - (y1 + t * ddy));
  }

  _pointToQuadraticDist(px: number, py: number, x1: number, y1: number, cpX: number, cpY: number, x2: number, y2: number): number {
    let minDist = Infinity;
    const steps = 20;
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const u = 1 - t;
      const qx = u * u * x1 + 2 * u * t * cpX + t * t * x2;
      const qy = u * u * y1 + 2 * u * t * cpY + t * t * y2;
      const d = Math.hypot(px - qx, py - qy);
      if (d < minDist) minDist = d;
    }
    return minDist;
  }

  selectNodes(ids: Array<number | string>): void {
    this._selectedNodes.clear();
    for (let i = 0; i < ids.length; i++) this._selectedNodes.add(ids[i]);
    this._engine.markDirty();
  }

  getSelectedNodes(): Array<number | string> {
    return Array.from(this._selectedNodes);
  }

  destroy(): void {
    if (!this._canvas || !this._attached) return;
    const b = this._bound;
    this._canvas.removeEventListener('pointerdown', b.pointerdown);
    this._canvas.removeEventListener('pointermove', b.pointermove);
    this._canvas.removeEventListener('pointerup', b.pointerup);
    this._canvas.removeEventListener('pointercancel', b.pointercancel);
    this._canvas.removeEventListener('wheel', b.wheel);
    this._attached = false;
  }
}
